import {
  Fuel, Wrench, MapPinned, Receipt,
  Eye, BookOpen, Navigation, ShoppingBag,
  Truck, Banknote, ClipboardList,
  PackageCheck, Undo2, FileMinus, Route,
} from 'lucide-react';
import type { ChildEntryType } from '../../types';
import type { ReceiptCaptureProfile } from '@/lib/receiptAiAnalysis';
import { getChildEntryFormRegistry } from '@/config/child-entry-form/configLoader';
import { resolveEntryTypeConfig } from '@/config/child-entry-form/configResolver';

/** Expense subtypes that map to unified `item_type: 'expense'` with `item_subtype` preserved */
export const EXPENSE_ENTRY_TYPES: ChildEntryType[] = ['toll', 'fuel', 'maintenance', 'purchase', 'delivery_note', 'supplier_invoice', 'supplier_quote', 'grn', 'purchase_return', 'supplier_credit', 'casual_wage', 'travel'];

export const DEFAULT_PAYMENT_METHODS: Partial<Record<ChildEntryType, string>> = {
  fuel: 'Company Card',
  toll: 'Company Card',
  maintenance: 'Company Card',
  purchase: 'Company Card',
  casual_wage: 'Cash',
  travel: 'Employee Paid (Seeking Reimbursement)',
};

export const PURCHASE_CATEGORY_OPTIONS = ['Parts', 'Supplies', 'Equipment', 'PPE', 'Food', 'Materials', 'Tools', 'Consumables', 'Travel', 'Meals', 'Utilities', 'Professional Services', 'Software', 'Other'];

export const CAPTURE_PROFILE_OPTIONS: Array<{ value: ReceiptCaptureProfile; label: string; helper: string }> = [
  { value: 'auto', label: 'Auto', helper: 'Let AI detect the layout' },
  { value: 'receipt', label: 'Till', helper: 'Narrow till slips' },
  { value: 'invoice', label: 'A4', helper: 'Invoices and tax pages' },
  { value: 'continuous', label: 'Continuous', helper: 'Dot-matrix / long paper' },
];

// Keep ChildEntryForm above the native-shell footer/pin controls (z-[90]).
// CameraModal still renders higher at z-[140].
export const CHILD_ENTRY_FORM_Z_INDEX = 200;

export interface EntryFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea' | 'date' | 'datetime';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  isCurrency?: boolean;
}

export interface EntryTypeConfig {
  label: string;
  icon: React.ElementType;
  accentColor: string;
  fields: EntryFieldConfig[];
}

export const ENTRY_CONFIG: Record<ChildEntryType, EntryTypeConfig> = {
  toll: {
    label: 'Toll Expense',
    icon: Receipt,
    accentColor: '#3b82f6',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'amount', label: 'Amount (ZAR)', type: 'number', required: true, placeholder: '0.00', isCurrency: true },
      { key: 'toll_name', label: 'Toll Name', type: 'text', placeholder: 'e.g. N1 Toll Plaza' },
      { key: 'payment_method', label: 'Payment Method', type: 'select', options: ['Company Card', 'Cash', 'Card', 'E-Tag', 'Account'] },
      { key: 'notes', label: 'Notes', type: 'text', placeholder: 'Optional notes' },
    ],
  },
  fuel: {
    label: 'Fuel Expense',
    icon: Fuel,
    accentColor: '#3b82f6',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'amount', label: 'Amount (ZAR)', type: 'number', required: true, placeholder: '0.00', isCurrency: true },
      { key: 'litres', label: 'Litres', type: 'number', required: true, placeholder: '0.0' },
      { key: 'fuel_type', label: 'Fuel Type', type: 'select', options: ['Diesel', 'Petrol 93', 'Petrol 95', 'LPG'] },
      { key: 'odometer', label: 'Odometer', type: 'number', placeholder: 'Current km' },
      { key: 'station', label: 'Station', type: 'text', placeholder: 'e.g. Shell N1 City' },
      { key: 'payment_method', label: 'Payment Method', type: 'select', options: ['Company Card', 'Card', 'Cash', 'Account', 'Fleet Card'] },
    ],
  },
  maintenance: {
    label: 'Vehicle Maintenance',
    icon: Wrench,
    accentColor: '#3b82f6',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'textarea', required: true, placeholder: 'Maintenance performed' },
      { key: 'maintenance_type', label: 'Type', type: 'select', options: ['Scheduled', 'Unscheduled', 'Tyre Change', 'Oil Top-up', 'Other'] },
      { key: 'amount', label: 'Cost (ZAR)', type: 'number', placeholder: '0.00', isCurrency: true },
      { key: 'odometer', label: 'Odometer', type: 'number', placeholder: 'Current km' },
      { key: 'payment_method', label: 'Payment Method', type: 'select', options: ['Company Card', 'Card', 'Cash', 'Account'] },
    ],
  },
  site_visit: {
    label: 'Site Visit',
    icon: MapPinned,
    accentColor: '#14b8a6',
    fields: [
      { key: 'site_name', label: 'Site Name', type: 'text', required: true, placeholder: 'Location name' },
      { key: 'purpose', label: 'Purpose', type: 'text', placeholder: 'Reason for visit' },
      { key: 'contact_person', label: 'Contact Person', type: 'text', placeholder: 'Who did you meet?' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Visit details' },
    ],
  },
  assessment: {
    label: 'Assessment',
    icon: ClipboardList,
    accentColor: '#7c3aed',
    fields: [
      { key: 'assessment_type', label: 'Type', type: 'text', required: true, placeholder: 'e.g. Safety, Quality' },
      { key: 'result', label: 'Result', type: 'select', options: ['Pass', 'Fail', 'Partial', 'N/A'] },
      { key: 'score', label: 'Score', type: 'number', placeholder: '0-100' },
      { key: 'notes', label: 'Findings', type: 'textarea', placeholder: 'Assessment findings' },
    ],
  },
  supervision: {
    label: 'Supervision',
    icon: Eye,
    accentColor: '#0891b2',
    fields: [
      { key: 'area', label: 'Area/Zone', type: 'text', required: true, placeholder: 'Supervised area' },
      { key: 'team_size', label: 'Team Size', type: 'number', placeholder: 'Number of people' },
      { key: 'notes', label: 'Observations', type: 'textarea', placeholder: 'Supervision notes' },
    ],
  },
  training: {
    label: 'Training',
    icon: BookOpen,
    accentColor: '#d97706',
    fields: [
      { key: 'topic', label: 'Topic', type: 'text', required: true, placeholder: 'Training topic' },
      { key: 'attendees', label: 'Attendees', type: 'number', placeholder: 'Number of attendees' },
      { key: 'duration_minutes', label: 'Duration (min)', type: 'number', placeholder: '0' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Training details' },
    ],
  },
  route_stop: {
    label: 'Route Stop',
    icon: Navigation,
    accentColor: '#3b82f6',
    fields: [
      { key: 'stop_name', label: 'Stop Name', type: 'text', required: true, placeholder: 'Stop location' },
      { key: 'purpose', label: 'Purpose', type: 'select', options: ['Rest', 'Loading', 'Unloading', 'Traffic', 'Weather', 'Other'] },
      { key: 'duration_minutes', label: 'Duration (min)', type: 'number', placeholder: '0' },
      { key: 'notes', label: 'Notes', type: 'text', placeholder: 'Optional notes' },
    ],
  },
  purchase: {
    label: 'Smart Expense Capture',
    icon: ShoppingBag,
    accentColor: '#3b82f6',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'What was purchased?' },
      { key: 'amount', label: 'Amount (ZAR)', type: 'number', required: true, placeholder: '0.00', isCurrency: true },
      { key: 'vendor', label: 'Vendor', type: 'text', placeholder: 'Where purchased' },
      { key: 'receipt_number', label: 'Receipt #', type: 'text', placeholder: 'Receipt number' },
      { key: 'category', label: 'Category', type: 'select', options: PURCHASE_CATEGORY_OPTIONS },
      { key: 'payment_method', label: 'Payment Method', type: 'select', options: ['Card', 'Cash', 'Account', 'Petty Cash', 'Company Card'] },
    ],
  },
  delivery_note: {
    label: 'Supplier Delivery Note',
    icon: Truck,
    accentColor: '#10b981',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'Delivery note description' },
      { key: 'delivery_number', label: 'Delivery Note #', type: 'text', placeholder: 'Delivery note number' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Delivery notes' },
    ],
  },
  supplier_invoice: {
    label: 'Supplier Invoice',
    icon: Receipt,
    accentColor: '#10b981',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'Invoice description' },
      { key: 'amount', label: 'Amount (ZAR)', type: 'number', required: true, placeholder: '0.00', isCurrency: true },
      { key: 'invoice_number', label: 'Invoice #', type: 'text', placeholder: 'Invoice number' },
      { key: 'due_date', label: 'Due Date', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Invoice notes' },
    ],
  },
  supplier_quote: {
    label: 'Supplier Quote',
    icon: Receipt,
    accentColor: '#6d28d9',
    fields: [
      { key: 'date', label: 'Quote Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'Quote description' },
      { key: 'quote_number', label: 'Quote #', type: 'text', placeholder: 'Quote reference number' },
      { key: 'valid_until', label: 'Valid Until', type: 'date' },
      { key: 'amount', label: 'Quoted Amount (ZAR)', type: 'number', placeholder: '0.00', isCurrency: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Quote notes' },
    ],
  },
  grn: {
    label: 'Goods Received Note',
    icon: PackageCheck,
    accentColor: '#10b981',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'GRN description' },
      { key: 'grn_number', label: 'GRN #', type: 'text', placeholder: 'Goods received note number' },
      { key: 'received_by', label: 'Received By', type: 'text', placeholder: 'Person who received goods' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Receiving notes' },
    ],
  },
  purchase_return: {
    label: 'Purchase Return',
    icon: Undo2,
    accentColor: '#ef4444',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'Return description' },
      { key: 'return_number', label: 'Return #', type: 'text', placeholder: 'Return reference number' },
      { key: 'return_reason', label: 'Return Reason', type: 'select', options: ['Damaged', 'Wrong Item', 'Quality Issue', 'Surplus', 'Expired', 'Other'] },
      { key: 'amount', label: 'Amount (ZAR)', type: 'number', placeholder: '0.00', isCurrency: true },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Return notes' },
    ],
  },
  supplier_credit: {
    label: 'Supplier Credit Note',
    icon: FileMinus,
    accentColor: '#ef4444',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'Credit note description' },
      { key: 'amount', label: 'Amount (ZAR)', type: 'number', required: true, placeholder: '0.00', isCurrency: true },
      { key: 'credit_note_number', label: 'Credit Note #', type: 'text', placeholder: 'Credit note number' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Credit note details' },
    ],
  },
  casual_wage: {
    label: 'Casual Wages',
    icon: Banknote,
    accentColor: '#8b5cf6',
    fields: [
      { key: 'date', label: 'Transaction Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Work Performed', type: 'text', required: true, placeholder: 'What the casuals did' },
      { key: 'amount', label: 'Amount Paid (ZAR)', type: 'number', required: true, placeholder: '0.00', isCurrency: true },
      { key: 'payment_method', label: 'Payment Method', type: 'select', options: ['Cash', 'Company Card', 'Bank Transfer', 'Petty Cash', 'Employee Paid (Seeking Reimbursement)', 'Other'] },
      { key: 'reimbursement_route', label: 'Reimbursement Route', type: 'select', options: ['Payroll', 'Bank Transfer', 'Petty Cash'], placeholder: 'Select if reimbursement is needed' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Names, hours, or any other details' },
    ],
  },
  travel: {
    label: 'Travel / Mileage',
    icon: Route,
    accentColor: '#6366f1',
    fields: [
      { key: 'date', label: 'Travel Date & Time', type: 'datetime', required: true },
      { key: 'description', label: 'Description', type: 'text', required: true, placeholder: 'e.g. Business trips for today' },
      { key: 'distance_km', label: 'Distance (km)', type: 'number', required: true, placeholder: '0.0' },
      { key: 'rate_per_km', label: 'SARS Rate / km (R)', type: 'number', required: true, placeholder: '0.00', isCurrency: true },
      { key: 'amount', label: 'Amount (ZAR)', type: 'number', required: true, placeholder: 'Auto-calculated', isCurrency: true },
      { key: 'vehicle_registration', label: 'Vehicle Registration', type: 'text', placeholder: 'e.g. ABC 123 GP' },
      { key: 'payment_method', label: 'Payment Method', type: 'select', options: ['Employee Paid (Seeking Reimbursement)', 'Company Card', 'Bank Transfer', 'Petty Cash', 'Other'] },
      { key: 'reimbursement_route', label: 'Reimbursement Route', type: 'select', options: ['Payroll', 'Bank Transfer', 'Petty Cash'], placeholder: 'Select if reimbursement is needed' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Trip details or notes' },
    ],
  },
};

/**
 * Check whether the AI-suggested category matches the declared entry type.
 * Returns { message, severity } where severity is 'blocking' for definite
 * mismatches (high confidence, wrong specific type) or null when acceptable.
 * General expense (purchase) and procurement types never block.
 */
export function getReceiptCategoryMismatchInfo(
  entryType: ChildEntryType,
  receipt: { suggested_category?: string; confidence?: number } | null,
): { message: string | null; severity: 'blocking' | null } {
  if (!receipt?.suggested_category || (receipt.confidence ?? 0) < 0.75) {
    return { message: null, severity: null };
  }

  const suggested = receipt.suggested_category.trim().toLowerCase();

  const expected = getReceiptCategoryExpected(entryType);
  if (!expected || expected.length === 0) {
    return { message: null, severity: null };
  }

  if (expected.some((cat) => suggested.includes(cat) || cat.includes(suggested))) {
    return { message: null, severity: null };
  }

  const prettySuggested = receipt.suggested_category.charAt(0).toUpperCase() + receipt.suggested_category.slice(1);
  const prettyEntry = getEntryTypeConfig(entryType)?.label || entryType;
  const generalExpenseLabel = getEntryTypeConfig('purchase')?.label || 'Smart Expense Capture';
  const message = `This receipt appears to be a ${prettySuggested} expense, not a ${prettyEntry}. Please capture it under ${generalExpenseLabel} instead.`;
  return { message, severity: 'blocking' };
}

export function getInitialFormData(entryType: ChildEntryType): Record<string, string> {
  const defaultPaymentMethod = getDefaultPaymentMethod(entryType);
  return defaultPaymentMethod ? { payment_method: defaultPaymentMethod } : {};
}

// ============================================================================
// REGISTRY-BACKED ACCESSORS (YAML → hardcoded fallback)
//
// These functions try the YAML registry (loaded from child-entry-configs.yaml)
// first, then fall back to the hardcoded constants above. This matches the
// pattern used by documentConfigs.ts → document-modal configLoader.
// ============================================================================

/**
 * Get the EntryTypeConfig for a given entry type.
 * Tries the YAML registry (source of truth) first, falls back to ENTRY_CONFIG.
 */
export function getEntryTypeConfig(entryType: ChildEntryType): EntryTypeConfig {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    const yamlConfig = registry.getEntryConfig(entryType);
    if (yamlConfig) {
      return resolveEntryTypeConfig(entryType, yamlConfig);
    }
  }
  return ENTRY_CONFIG[entryType];
}

/**
 * Get the list of expense entry types.
 * Tries the YAML registry first, falls back to EXPENSE_ENTRY_TYPES.
 */
export function getExpenseEntryTypes(): ChildEntryType[] {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    const yamlTypes = registry.getExpenseEntryTypes();
    if (yamlTypes.length > 0) {
      return yamlTypes as ChildEntryType[];
    }
  }
  return EXPENSE_ENTRY_TYPES;
}

/**
 * Get the default payment method for an entry type.
 * Tries the YAML registry first, falls back to DEFAULT_PAYMENT_METHODS.
 */
export function getDefaultPaymentMethod(entryType: ChildEntryType): string | undefined {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    const yamlMethod = registry.getDefaultPaymentMethod(entryType);
    if (yamlMethod) return yamlMethod;
  }
  return DEFAULT_PAYMENT_METHODS[entryType];
}

/**
 * Get the purchase category options.
 * Tries the YAML registry first, falls back to PURCHASE_CATEGORY_OPTIONS.
 */
export function getPurchaseCategoryOptions(): string[] {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    const yamlOptions = registry.getPurchaseCategoryOptions();
    if (yamlOptions.length > 0) return yamlOptions;
  }
  return PURCHASE_CATEGORY_OPTIONS;
}

/**
 * Get the capture profile options.
 * Tries the YAML registry first, falls back to CAPTURE_PROFILE_OPTIONS.
 */
export function getCaptureProfileOptions(): Array<{ value: ReceiptCaptureProfile; label: string; helper: string }> {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    const yamlOptions = registry.getCaptureProfileOptions();
    if (yamlOptions.length > 0) {
      return yamlOptions as Array<{ value: ReceiptCaptureProfile; label: string; helper: string }>;
    }
  }
  return CAPTURE_PROFILE_OPTIONS;
}

/**
 * Get the child entry form z-index.
 * Tries the YAML registry first, falls back to CHILD_ENTRY_FORM_Z_INDEX.
 */
export function getChildEntryFormZIndex(): number {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    return registry.getChildEntryFormZIndex();
  }
  return CHILD_ENTRY_FORM_Z_INDEX;
}

/**
 * Get the receipt category expected keywords for an entry type.
 * Tries the YAML registry first, falls back to the hardcoded expectedMap.
 */
export function getReceiptCategoryExpected(entryType: ChildEntryType): string[] | undefined {
  const registry = getChildEntryFormRegistry();
  if (registry.isInitialized()) {
    return registry.getReceiptCategoryExpected(entryType);
  }
  // Fallback: replicate the hardcoded expectedMap from getReceiptCategoryMismatchInfo
  const expectedMap: Record<string, string[]> = {
    fuel: ['fuel'],
    toll: ['toll'],
    maintenance: ['maintenance', 'parts', 'supplies', 'tools', 'equipment', 'consumables', 'roadside'],
    purchase: [],
    casual_wage: [],
    delivery_note: ['delivery', 'shipping', 'freight', 'goods', 'receiving', 'grn', 'goods_received'],
    supplier_invoice: ['invoice', 'supplier', 'vendor', 'bill', 'purchase', 'accounts_payable'],
    supplier_quote: ['quote', 'quotation', 'estimate', 'proposal'],
    grn: ['grn', 'goods_received', 'receiving', 'delivery', 'shipping', 'freight', 'goods'],
    purchase_return: ['return', 'refund', 'credit', 'purchase_return', 'goods_return'],
  };
  return expectedMap[entryType];
}
